import { Link, useParams } from "@tanstack/react-router"
import { FileQuestion, Lock, Table2 } from "lucide-react"
import { useMemo } from "react"
import { Trans } from "react-i18next"
import { Mono } from "@/components/copy-button"
import { DefinitionSummary } from "@/components/definition-summary"
import { JsonView } from "@/components/json-view"
import { EmptyState, ErrorState } from "@/components/states"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { PageHeader } from "@/components/ui/page-header"
import { Screen } from "@/components/ui/screen"
import { Section } from "@/components/ui/section"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { artifactTitle, entityDef, kindLabel, referencesTo } from "@/lib/defs"
import { useFormatNumber, useT } from "@/lib/i18n"
import { useDefinitions } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { DefinitionSource, definitionOrigin } from "@/data/definition-source"

export function DefinitionDetail() {
  const t = useT()
  const formatNumber = useFormatNumber()
  const scope = useScope()
  const { kind, artifactId } = useParams({ from: "/$orgId/$environmentId/definitions/$kind/$artifactId" })
  const defs = useDefinitions(scope)
  const artifact = useMemo(
    () => defs.data?.artifacts.find((a) => a.kind === kind && a.artifactId === artifactId),
    [defs.data, kind, artifactId],
  )
  const references = useMemo(
    () => (artifact && defs.data ? referencesTo(artifact, defs.data.artifacts) : []),
    [artifact, defs.data],
  )

  if (defs.isPending) {
    return (
      <div className="space-y-3 px-4 py-5 md:px-8 md:py-7">
        <Skeleton className="h-5 w-64" />
        <Skeleton className="h-3 w-80" />
        <Skeleton className="mt-8 h-40 w-full" />
      </div>
    )
  }
  if (defs.isError)
    return <ErrorState error={defs.error} what={t("definitionDetail.what")} onRetry={() => void defs.refetch()} />
  if (!artifact) {
    return (
      <EmptyState icon={<FileQuestion />} title={t("definitionDetail.notInstalledTitle")}>
        <p>
          <Trans
            shouldUnescape
            tOptions={{ interpolation: { escapeValue: true } }}
            t={t}
            i18nKey="definitionDetail.notInstalledDescription"
            values={{ artifactId, kind, version: defs.data.version }}
            components={{ artifact: <code /> }}
          />
        </p>
      </EmptyState>
    )
  }
  const def = artifact.kind === "entity" ? entityDef(artifact) : null
  // The Source tab exists only when the cell retained an origin for this definition.
  const origin = definitionOrigin(artifact)
  return (
    <Screen width="wide" role="article">
      <header>
        <PageHeader
          identity={
            <>
              <h1 className="text-base font-medium">{artifactTitle(artifact)}</h1>
              <Badge tone="outline">{def && def.form !== "objectType" ? def.form : artifact.kind}</Badge>
              {artifact.lifecycle ? <Badge>{artifact.lifecycle}</Badge> : null}
            </>
          }
          actions={
            artifact.kind === "rule" && artifact.layer === "tenant" && artifact.payload.ruleVersion === "2.0" ? (
              <Button size="sm" variant="primary" asChild>
                <Link
                  to="/$orgId/$environmentId/decisions/$artifactId"
                  params={{ ...scope, artifactId: artifact.artifactId }}
                >
                  <Table2 /> {t("definitionDetail.editDecisionTable")}
                </Link>
              </Button>
            ) : null
          }
          description={def?.description}
        />
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <Mono value={artifact.artifactId} />
          <span className="font-mono tabular">v{artifact.version}</span>
          {artifact.namespace ? (
            <span>{t("definitionDetail.namespace", { namespace: artifact.namespace })}</span>
          ) : null}
        </div>
        {artifact.layer === "registry" ? (
          <p className="mt-4 flex max-w-[72ch] items-start gap-2 rounded-md border border-border-subtle bg-card px-3 py-2 text-xs text-muted-foreground">
            <Lock className="mt-0.5 size-3.5 shrink-0 text-subtle-foreground" aria-hidden />
            <span>
              {defs.data.registry.length === 1 ? (
                <Trans
                  shouldUnescape
                  tOptions={{ interpolation: { escapeValue: true } }}
                  t={t}
                  i18nKey="definitionDetail.registryRelease"
                  values={{ release: defs.data.registry[0] ?? "" }}
                  components={{ release: <code className="font-mono" /> }}
                />
              ) : (
                t("definitionDetail.registryReleases")
              )}
            </span>
          </p>
        ) : null}
      </header>
      <Tabs defaultValue="summary" className="mt-6">
        <TabsList>
          <TabsTrigger value="summary">{t("definitionDetail.summary")}</TabsTrigger>
          {origin ? <TabsTrigger value="source">{t("dv.source.tab")}</TabsTrigger> : null}
          <TabsTrigger value="json">{t("definitionDetail.canonicalJson")}</TabsTrigger>
          <TabsTrigger value="refs">
            {t("definitionDetail.referencedBy")}{" "}
            <span className="text-xs text-subtle-foreground tabular">{formatNumber(references.length)}</span>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="summary" className="pt-6">
          <Section>
            <DefinitionSummary artifact={artifact} />
          </Section>
        </TabsContent>
        {origin ? (
          <TabsContent value="source" className="pt-6">
            <DefinitionSource artifact={artifact} origin={origin} />
          </TabsContent>
        ) : null}
        <TabsContent value="json" className="pt-6">
          <JsonView value={artifact.raw} className="max-h-[70vh]" />
        </TabsContent>
        <TabsContent value="refs" className="pt-6">
          {references.length === 0 ? (
            <p className="text-sm text-subtle-foreground">{t("definitionDetail.noReferences")}</p>
          ) : (
            <ul className="divide-y divide-border-subtle overflow-hidden rounded-lg border border-border-subtle">
              {references.map((r) => (
                <li key={`${r.artifactId}@${r.version}`}>
                  <Link
                    to="/$orgId/$environmentId/definitions/$kind/$artifactId"
                    params={{ ...scope, kind: r.kind, artifactId: r.artifactId }}
                    className="flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-raised"
                  >
                    <span className="w-28 shrink-0 text-xs text-muted-foreground">{kindLabel(r.kind, t)}</span>
                    <span className="truncate">{artifactTitle(r)}</span>
                    <code className="ms-auto truncate font-mono text-2xs text-subtle-foreground">{r.artifactId}</code>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </Screen>
  )
}
